import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import { EAiIntegrationProvider } from "../../Objects/AiIntegration/EAiIntegrationProvider"
import { IAiIntegrationSettings } from "../../Objects/AiIntegration/IAiIntegrationSettings"
import { IAiQuestionResponse } from "../../Objects/AiIntegration/IAiQuestionResponse"

type ApiWrappedResponse<T> = {
  success?: boolean
  message?: string
  data?: T
}

export const defaultAiIntegrationSettings: IAiIntegrationSettings = {
  isEnabled: false,
  provider: EAiIntegrationProvider.Gpt,
  endpointUrl: "https://api.openai.com/v1/chat/completions",
  apiKey: "",
  model: "gpt-4o-mini",
  systemPrompt:
    "Analise os dados do restaurante com foco em faturamento, margem, recorrência, cardápio, operação e próximos investimentos. Responda em português do Brasil, com recomendações práticas e sem inventar dados.",
  monthsToAnalyze: 24,
}

export const useAiIntegration = () => {
  const { t } = useTranslation("general")
  const { enableLoad, disableLoad } = useLoad()
  const { setError } = useError()

  const resolveErrorMessage = (message?: string) => {
    const trimmed = message?.trim()

    return trimmed && trimmed.length > 0 ? trimmed : t("label_unknown_error_description")
  }

  function unwrapResponseData<T>(response: unknown, fallbackValue: T): T {
    const wrapped = response as ApiWrappedResponse<T>

    if (typeof response === "object" && response !== null && "success" in response) {
      if (wrapped.success === false) {
        throw new Error(resolveErrorMessage(wrapped.message))
      }

      return (wrapped.data ?? fallbackValue) as T
    }

    return (response ?? fallbackValue) as T
  }

  const getSettings = async (): Promise<IAiIntegrationSettings> => {
    enableLoad()

    try {
      const response = await GetRequest<IAiIntegrationSettings>(API_ROUTES.AI_INTEGRATION.SETTINGS)

      return { ...defaultAiIntegrationSettings, ...unwrapResponseData(response, defaultAiIntegrationSettings) }
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return defaultAiIntegrationSettings
    } finally {
      disableLoad()
    }
  }

  const saveSettings = async (payload: IAiIntegrationSettings): Promise<IAiIntegrationSettings> => {
    enableLoad()

    try {
      const response = await PutRequest<IAiIntegrationSettings>(API_ROUTES.AI_INTEGRATION.SETTINGS, payload)

      return unwrapResponseData(response, payload)
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  const ask = async (question: string): Promise<IAiQuestionResponse> => {
    enableLoad()

    try {
      const response = await PostRequest<IAiQuestionResponse>(API_ROUTES.AI_INTEGRATION.ASK, { question })

      return unwrapResponseData(response, {
        answer: "",
        provider: "",
        model: "",
        contextMonths: 0,
        generatedAt: new Date().toISOString(),
      })
    } catch (e: unknown) {
      const message = e instanceof Error ? resolveErrorMessage(e.message) : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
      throw e
    } finally {
      disableLoad()
    }
  }

  return { getSettings, saveSettings, ask }
}
